import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  Bot,
  Award,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Focus,
  Brain,
  Mail,
} from "lucide-react";
import SearchBox from "./SearchBox";
import ContactModal from "./ContactModal";
import Footer from "./Footer";
// Removed EmailJS import - now using Netlify Forms

interface CourseModuleProps {
  number: number;
  title: string;
  description: string;
  duration: string;
  topics: string[];
}

interface CourseTemplateProps {
  title: string;
  subtitle: string;
  description: string;
  imageUrl: string;
  duration: string;
  level: string;
  students: string;
  modules: CourseModuleProps[];
}

export default function CourseTemplate({
  title,
  subtitle,
  description,
  imageUrl,
  duration,
  level,
  students,
  modules,
}: CourseTemplateProps) {
  const [expandedFaqs, setExpandedFaqs] = useState<Record<number, boolean>>({});
  const [showSearch, setShowSearch] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    setExpandedFaqs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <div className="flex-shrink-0 bg-gray-100 rounded-md p-2">
                <Bot className="h-6 w-6 text-black" />
              </div>
              <span className="ml-3 text-xl font-semibold text-gray-900">
                One80Labs
              </span>
            </Link>
            <div className="hidden md:block">
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Home
                </Link>
                <Link
                  to="/courses"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Training
                </Link>
                <Link
                  to="/fractional-pm"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Fractional PM Services
                </Link>
                <Link
                  to="/blog"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Blog
                </Link>
                <Link
                  to="/about"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  About Us
                </Link>
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium inline-flex items-center"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          {showSearch && (
            <div className="absolute left-0 right-0 bg-white px-4 py-3 border-t border-gray-200 shadow-lg">
              <div className="max-w-3xl mx-auto">
                <SearchBox />
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left Column - Course Content */}
          <div className="lg:w-2/3">
            <Link
              to="/courses"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to All Courses
            </Link>

            <h1 className="text-3xl font-bold text-gray-900 mb-6">{title}</h1>

            {imageUrl && (
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-64 object-cover rounded-lg mb-8"
              />
            )}

            <div className="prose max-w-none mb-12">
              <p className="text-gray-600">{description}</p>
            </div>

            {/* Course Modules */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Course Modules
              </h2>
              <div className="space-y-4">
                {modules.map((module) => (
                  <div
                    key={module.number}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="font-medium text-gray-900">
                        Module {module.number}: {module.title}
                      </h3>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-600 mb-3">{module.description}</p>
                      <div className="flex items-center text-gray-500 mb-4">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{module.duration}</span>
                      </div>
                      <div className="space-y-2">
                        {module.topics.map((topic, index) => (
                          <div key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-[#F98B3D] mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{topic}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {/* FAQ Item 1 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(1)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Do I need technical skills to take this course?
                    </h3>
                    {expandedFaqs[1] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[1] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        No technical background is required. This course is
                        designed for professionals from all fields who want to
                        enhance their productivity using AI tools. Basic
                        computer skills and familiarity with office software are
                        sufficient.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ Item 2 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(2)}
                  >
                    <h3 className="font-medium text-gray-900">
                      What tools will we be using during the workshop?
                    </h3>
                    {expandedFaqs[2] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[2] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        We'll primarily work with ChatGPT, Gemini, and Microsoft
                        365 Co-Pilot. We'll also introduce other specialized AI
                        tools depending on the specific needs of participants.
                        All tools used either have free tiers or trial versions
                        available.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ Item 3 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(3)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Will I receive any materials or resources?
                    </h3>
                    {expandedFaqs[3] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[3] && (
                    <div className="p-4 text-gray-600">
                      <p>Yes, all participants receive:</p>
                      <ul className="mt-2 space-y-2 list-disc list-inside">
                        <li>Comprehensive digital workbook</li>
                        <li>Prompt templates and cheat sheets</li>
                        <li>Access to private community for ongoing support</li>
                        <li>Certificate of completion</li>
                        <li>Lifetime access to course updates</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* FAQ Item 4 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(4)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Can this course be customized for my organization?
                    </h3>
                    {expandedFaqs[4] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[4] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        Absolutely! We offer customized versions of this
                        workshop for teams and organizations, tailored to your
                        specific industry, challenges, and goals. Contact us to
                        discuss your needs and receive a custom proposal.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Meet Your Instructor */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Meet Your Instructor
              </h2>
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <img
                      src="https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png"
                      alt="Nick Kramer"
                      className="rounded-lg w-full h-auto"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Nick Kramer
                    </h3>
                    <p className="text-gray-600 italic mb-4">
                      AI Productivity Specialist & Technology Educator
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Focus className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          Nick is a technology educator and AI specialist with
                          over 15 years of experience helping professionals and
                          organizations leverage emerging technologies to
                          enhance productivity and innovation.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Brain className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          As the founder of One80Labs and One80Training, Nick
                          has worked with businesses across industries—ranging
                          from finance and healthcare to manufacturing and
                          tech—guiding teams in leveraging AI for automation,
                          data analysis, and workflow optimization.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Award className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          Certified in AI-powered product thinking and
                          human-centered design, Nick specializes in making AI
                          accessible to non-technical professionals, ensuring
                          they can harness its power for smarter decision-making
                          and productivity gains.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Course Details */}
          <div className="lg:w-1/3">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Course Details
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Next Sessions
                        </h4>
                        <p className="text-gray-600">
                          June 15-16, 2025 (9am-1pm)
                        </p>
                        <p className="text-gray-600">July 20, 2025 (9am-5pm)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Participants
                        </h4>
                        <p className="text-gray-600">{students}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MessageCircle className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Format</h4>
                        <p className="text-gray-600">
                          In-person or virtual options
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Duration</h4>
                        <p className="text-gray-600">{duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Level</h4>
                        <p className="text-gray-600">{level}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Have Questions?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Contact us for more information about this course or to
                    discuss custom training options for your organization.
                  </p>
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        // Using Netlify Forms - no onSubmit prop needed
      />

      <Footer />
    </div>
  );
}
